/**
 * @file authApi.js
 * @description API utility functions for user authentication, including login and password recovery.
 *              Handles HTTP requests to various endpoints using Axios.
 *              Each function returns the Axios response or a custom error object for external handling.
 * @author Govind
 * @date 29 January 2025
 */

import axios from "axios";
import apiUrl from "./api";

/**
 * @function getLogin
 * @description Sends user credentials to the login endpoint for authentication.
 * @param {string} username - The username or email of the user.
 * @param {string} password - The user's password.
 * @returns {Promise<Object>} - Axios response object if successful, custom error object if request fails.
 * @author Govind Dindewad
 */
async function getLogin(username, password) {
    const data = {
        userName: username,
        userPassword: password
    };

    try {
        const response = await axios.post(`${apiUrl}user/login`, data);
        return response; // Return the response object for further processing.
    } catch (err) {
        if (err?.message === 'Network Error') {
            // Return a custom object for network errors.
            return {
                status: 301,
                message: 'Network Error'
            };
        }
        return err; // Propagate the error for external handling.
    }
}

/**
 * @function forgotPassword
 * @description Initiates a password reset process for the specified username.
 * @param {string} username - The username of the account to recover.
 * @returns {Promise<Object>} - Axios response object if successful, custom error object if request fails.
 * @author Govind Dindewad
 */
async function forgotPassword(username) {
    const data = { userName: username };

    try {
        const response = await axios.post(`${apiUrl}user/forget-password`, data);
        return response;
    } catch (err) {
        if (err?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            };
        }
        return err;
    }
}

/**
 * @function sendOTP
 * @description Sends an OTP to the user's mobile number.
 * @param {string} mobileNo - The mobile number to which the OTP is sent.
 * @returns {Promise<Object>} - Axios response object if successful, custom error object if request fails.
 * @author Govind Dindewad
 */
async function sendOTP(mobileNo) {
    const data = { userMobileNumber: mobileNo };

    try {
        const response = await axios.post(`${apiUrl}user/send-otp`, data);
        return response;
    } catch (err) {
        if (err?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            };
        }
        return err;
    }
}

/**
 * @function verifyOTP
 * @description Verifies the OTP entered by the user.
 * @param {Object} data - An object containing `userMobileNumber` and `otp`.
 * @returns {Promise<Object>} - Axios response object if successful, custom error object if request fails.
 * @author Govind Dindewad
 */
async function verifyOTP(data) {
    try {
        const response = await axios.post(`${apiUrl}user/verify-otp`, data);
        return response;
    } catch (err) {
        if (err?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            };
        }
        return err;
    }
}

/**
 * @function updatePassword
 * @description Updates the user's password using their OTP and new password.
 * @param {Object} data - An object containing `userMobileNumber`, `otp`, and `userPassword`.
 * @returns {Promise<Object>} - Axios response object if successful, custom error object if request fails.
 * @author Govind Dindewad
 */
async function updatePassword(data) {
    try {
        const response = await axios.patch(`${apiUrl}user/update-password`, data);
        return response;
    } catch (err) {
        if (err?.message === 'Network Error') {
            return {
                status: 301,
                message: 'Network Error'
            };
        }
        return err;
    }
}

// Export authentication API functions for use in other parts of the application.
export { getLogin, forgotPassword, sendOTP, verifyOTP, updatePassword };
